import { flatten } from 'lodash';
import {
  BodyMeasurement,
  ExerciseType,
  getClosestDataPointBeforeTargetDate,
  BaseExerciseTemplate,
  OwnedWorkoutExerciseSet,
} from '.';

export const isVolumeDoublingSupportableForExerciseTemplate = (
  exerciseTemplate: Pick<BaseExerciseTemplate, 'equipment_category' | 'exercise_type'>,
): boolean => {
  return (
    exerciseTemplate.equipment_category === 'dumbbell' &&
    exerciseTemplate.exercise_type === 'weight_reps'
  );
};

export interface VolumeCalculableWorkout {
  exercises: {
    exercise_type: ExerciseType;
    exercise_template_id: string;
    volume_doubling_enabled: boolean;
    hundred_percent_bodyweight_exercise: boolean;
    sets: Pick<
      OwnedWorkoutExerciseSet,
      'indicator' | 'weight_kg' | 'reps' | 'distance_meters' | 'duration_seconds'
    >[];
  }[];
  include_warmup_sets: boolean;
}

export interface TimestampedVolumeCalculableWorkout extends VolumeCalculableWorkout {
  start_time: number;
}

interface WorkoutVolumeKgProps {
  workout: TimestampedVolumeCalculableWorkout;
  measurements: Pick<BodyMeasurement, 'date' | 'weight_kg'>[];
}

export type BodyWeightMeasurement = BodyMeasurement & Required<Pick<BodyMeasurement, 'weight_kg'>>;

export const workoutVolumeKg = ({ workout, measurements }: WorkoutVolumeKgProps) => {
  const workoutDate = new Date(workout.start_time * 1000);
  /** We consider only measurements that include a body weight */
  const bodyWeightMeasurements = measurements.filter(
    (m): m is BodyWeightMeasurement => !!m.weight_kg,
  );

  /** latest logged bodyweight that is older than the workout itself */
  const mostRecentMeasurementBeforeWorkout = getClosestRelevantBodyWeightKgForDate(
    bodyWeightMeasurements,
    workoutDate,
  );

  /**
   * there are no bodyweights logged *before* the workout, so take the oldest
   * logged bodyweight, even though it's newer than the workout, as that's the
   * closest approximation that we've got. if there is nothing logged at all,
   * then we have to take 0
   */
  const oldestLoggedMeasurement = bodyWeightMeasurements[0]?.weight_kg ?? 0;

  const bodyweightKg = mostRecentMeasurementBeforeWorkout ?? oldestLoggedMeasurement;

  return workoutVolumeSetsVolumeKg({
    bodyweightKg,
    sets: workoutToWorkoutVolumeSets(workout),
  });
};

interface EstimatedWorkoutVolumeKgProps {
  workout: VolumeCalculableWorkout;
  bodyweightKg: number;
}

export const estimatedWorkoutVolumeKg = ({
  workout,
  bodyweightKg,
}: EstimatedWorkoutVolumeKgProps) => {
  return workoutVolumeSetsVolumeKg({
    bodyweightKg,
    sets: workoutToWorkoutVolumeSets(workout),
  });
};

/**
 * Given a date, this will return the nearest bodyweight value
 * that was logged before it.
 */
export const getClosestRelevantBodyWeightKgForDate = (
  bodyWeightMeasurements: BodyWeightMeasurement[],
  date: Date,
): number | undefined => {
  return getClosestDataPointBeforeTargetDate(
    bodyWeightMeasurements,
    (m: BodyWeightMeasurement) => new Date(m.date),
    date,
  )?.weight_kg;
};

interface WorkoutVolumeSetsVolumeKgProps {
  bodyweightKg: number; // Pass 0 if it's not known
  sets: WorkoutVolumeSet[];
}

const workoutVolumeSetsVolumeKg = ({
  bodyweightKg,
  sets,
}: WorkoutVolumeSetsVolumeKgProps): number => {
  return sets.reduce((accu, s) => {
    return accu + setVolumeKg(s, bodyweightKg);
  }, 0);
};

export interface WorkoutVolumeSet {
  type: ExerciseType;
  exerciseTemplateId: string;
  weightKg: number;
  reps: number;
  distanceMeters: number;
  volumeDoublingEnabled?: boolean;
  hundredPercentBodyweightExercise: boolean;
}

export const setVolumeKg = (set: WorkoutVolumeSet, bodyweightKg: number): number => {
  const { type, weightKg: originalWeightKg, reps, distanceMeters, volumeDoublingEnabled } = set;

  const weightKg = originalWeightKg * (volumeDoublingEnabled ? 2 : 1);

  switch (type) {
    case 'weight_reps':
      // eg. Bench Press (Barbell)
      return weightKg * reps;
    case 'reps_only':
      if (set.hundredPercentBodyweightExercise) {
        // eg. Pull Up
        return bodyweightKg * reps;
      } else {
        // eg. Push Up
        return 0;
      }
    case 'bodyweight_reps':
      if (set.hundredPercentBodyweightExercise) {
        // eg. Pull Up (Weighted)
        return (weightKg + bodyweightKg) * reps;
      } else {
        // eg. Push Up (Weighted)
        return weightKg * reps;
      }
    case 'bodyweight_assisted_reps':
      if (set.hundredPercentBodyweightExercise) {
        // eg. Pull Up (Assisted)
        const bodyweightAssistedRepsVolume = (bodyweightKg - weightKg) * reps;
        return Math.max(bodyweightAssistedRepsVolume, 0);
      } else {
        // e.g. a custom `bodyweight_assisted_reps` exercise
        return 0;
      }
    case 'short_distance_weight':
      // eg. Farmers Walk
      return distanceMeters * weightKg;
    case 'weight_duration':
      return weightKg;
    default:
      // `duration` and `distance_duration` exercises
      return 0;
  }
};

const workoutToWorkoutVolumeSets = (workout: VolumeCalculableWorkout) => {
  const unflatSets = workout.exercises.map(e => {
    return e.sets
      .filter(s => workout.include_warmup_sets || s.indicator !== 'warmup')
      .map(s => {
        return {
          type: e.exercise_type || 'weight_reps',
          exerciseTemplateId: e.exercise_template_id,
          weightKg: s.weight_kg ?? 0,
          reps: s.reps ?? 0,
          distanceMeters: s.distance_meters ?? 0,
          volumeDoublingEnabled: e.volume_doubling_enabled,
          hundredPercentBodyweightExercise: e.hundred_percent_bodyweight_exercise,
        };
      });
  });

  const sets: WorkoutVolumeSet[] = flatten(unflatSets);

  return sets;
};
